/**
 * Contract Status Import — bulk-update agent carrier appointment statuses
 * from a carrier/portal report (e.g. an RTS export).
 *
 * Wizard: Upload → Map columns → Reconcile (carriers + unknown agents) →
 * Review & Commit. KR admins only (enforced server-side).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Upload, FileSpreadsheet, ArrowRight, ArrowLeft, CheckCircle2, AlertTriangle, Undo2, ListChecks } from 'lucide-react';
import { contractStatusImportAPI, carrierAPI, agentAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

export default function ContractStatusImportPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState('upload'); // upload | map | reconcile | done
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState('');
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [fields, setFields] = useState([]);
  const [mapping, setMapping] = useState({});
  const [carriers, setCarriers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [carrierOverrides, setCarrierOverrides] = useState({}); // raw name → carrier_id
  const [agentOverrides, setAgentOverrides] = useState({});     // report npn → target npn
  const [agentQuery, setAgentQuery] = useState({});             // npn → search string
  const [agentResults, setAgentResults] = useState({});         // npn → [agents]
  const [createMissing, setCreateMissing] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [reviewCount, setReviewCount] = useState(0);

  const loadHistory = useCallback(() => {
    contractStatusImportAPI.history().then((r) => setHistory(r.data?.rows || [])).catch(() => {});
  }, []);

  const loadReviewCount = useCallback(() => {
    contractStatusImportAPI.review().then((r) => setReviewCount(r.data?.total ?? (r.data?.rows || []).length)).catch(() => {});
  }, []);

  const revertImport = useCallback(async (h) => {
    if (h.reverted) return;
    if (!window.confirm(`Revert this import (${h.filename})?\n\nThis restores the previous status on ${h.updated} updated appointment(s) and removes ${h.created || 0} that were created. Appointments changed by a newer import are left untouched.`)) return;
    try {
      const r = await contractStatusImportAPI.revertImport(h.batch_id);
      const d = r.data || {};
      toast.success(`Reverted — ${d.reverted_updates} restored, ${d.reverted_creates} removed${d.skipped ? `, ${d.skipped} skipped` : ''}`);
      loadHistory();
      loadReviewCount();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Revert failed'));
    }
  }, [loadHistory, loadReviewCount]);

  useEffect(() => {
    carrierAPI.list().then((r) => setCarriers(r.data || [])).catch(() => {});
    loadHistory();
    loadReviewCount();
  }, [loadHistory, loadReviewCount]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await contractStatusImportAPI.preview(fd);
      setColumns(r.data.columns || []);
      setRows(r.data.rows || []);
      setFields(r.data.fields || []);
      setMapping(r.data.suggested_mapping || {});
      setStep('map');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not read file'));
    } finally { setBusy(false); e.target.value = ''; }
  };

  const runResolve = useCallback(async () => {
    setBusy(true);
    try {
      const r = await contractStatusImportAPI.resolve({
        mapping, rows, carrier_overrides: carrierOverrides, agent_overrides: agentOverrides,
      });
      setSummary(r.data);
      setStep('reconcile');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Resolve failed'));
    } finally { setBusy(false); }
  }, [mapping, rows, carrierOverrides, agentOverrides]);

  const commit = async () => {
    setBusy(true);
    try {
      const r = await contractStatusImportAPI.commit({
        mapping, rows, carrier_overrides: carrierOverrides, agent_overrides: agentOverrides,
        create_missing: createMissing, filename: fileName,
      });
      setResult(r.data);
      setStep('done');
      loadReviewCount();
      const u = r.data.updated || 0, cr = r.data.created || 0;
      toast.success(`Applied — ${u} updated, ${cr} created`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Commit failed'));
    } finally { setBusy(false); }
  };

  const searchAgent = async (npn, q) => {
    setAgentQuery((s) => ({ ...s, [npn]: q }));
    if (!q || q.length < 2) { setAgentResults((s) => ({ ...s, [npn]: [] })); return; }
    try {
      const r = await agentAPI.list({ search: q, limit: 8 });
      const list = r.data?.agents || r.data || [];
      setAgentResults((s) => ({ ...s, [npn]: Array.isArray(list) ? list.slice(0, 8) : [] }));
    } catch { /* ignore */ }
  };

  const reset = () => {
    setStep('upload'); setFileName(''); setColumns([]); setRows([]); setFields([]);
    setMapping({}); setSummary(null); setCarrierOverrides({}); setAgentOverrides({}); setResult(null);
  };

  const allCarriersResolved = useMemo(
    () => (summary?.unmatched_carriers || []).every((c) => carrierOverrides[c.raw]),
    [summary, carrierOverrides],
  );

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto" data-testid="contract-status-import-page">
      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
        <div className="flex items-center gap-2.5">
          <div className="h-10 w-10 rounded-xl bg-indigo-600/10 grid place-items-center">
            <Upload className="h-5 w-5 text-indigo-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Import Contract Status</h1>
            <p className="text-sm text-slate-500">Bulk-update agent carrier appointment statuses from a portal report</p>
          </div>
        </div>
        <Button
          variant={reviewCount > 0 ? 'default' : 'outline'}
          onClick={() => navigate('/contracts/import-status/review')}
          data-testid="csi-open-review-btn"
          className={reviewCount > 0 ? 'bg-amber-600 hover:bg-amber-700' : ''}
        >
          <ListChecks size={15} className="mr-1.5" />
          Review queue
          {reviewCount > 0 && (
            <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-white/25 text-xs font-semibold" data-testid="csi-review-count">
              {reviewCount}
            </span>
          )}
        </Button>
      </div>

      <Stepper step={step} />

      {step === 'upload' && (
        <Card><CardContent className="py-6">
          <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-xl py-12 cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/40 transition-colors" data-testid="csi-dropzone">
            {busy ? <Loader2 className="h-8 w-8 animate-spin text-indigo-600" /> : <FileSpreadsheet className="h-8 w-8 text-slate-400" />}
            <span className="text-sm font-medium text-slate-700">{busy ? 'Reading file…' : 'Click to choose an Excel or CSV report'}</span>
            <span className="text-xs text-slate-400">.xlsx, .xls or .csv</span>
            <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onFile} data-testid="csi-file-input" />
          </label>
        </CardContent></Card>
      )}

      {step === 'upload' && history.length > 0 && (
        <Card className="mt-4" data-testid="csi-history">
          <CardHeader><CardTitle className="text-base">Recent imports</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b">
                    <th className="py-1.5 pr-3">Date</th>
                    <th className="py-1.5 pr-3">File</th>
                    <th className="py-1.5 pr-3">By</th>
                    <th className="py-1.5 pr-3 text-right">Updated</th>
                    <th className="py-1.5 pr-3 text-right">Created</th>
                    <th className="py-1.5 pr-3 text-right">Unchanged</th>
                    <th className="py-1.5 pr-3 text-right">Review</th>
                    <th className="py-1.5 pr-3 text-right">Skipped</th>
                    <th className="py-1.5 pr-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody data-testid="csi-history-rows">
                  {history.map((h) => (
                    <tr key={h.batch_id} className="border-b last:border-0">
                      <td className="py-1.5 pr-3 whitespace-nowrap text-slate-600">{new Date(h.created_at).toLocaleString()}</td>
                      <td className="py-1.5 pr-3 max-w-[180px] truncate" title={h.filename}>{h.filename}</td>
                      <td className="py-1.5 pr-3 text-slate-500 max-w-[160px] truncate">{h.created_by}</td>
                      <td className="py-1.5 pr-3 text-right font-mono text-emerald-700">{h.updated}</td>
                      <td className="py-1.5 pr-3 text-right font-mono text-emerald-700">{h.created ?? 0}</td>
                      <td className="py-1.5 pr-3 text-right font-mono text-slate-500">{h.unchanged}</td>
                      <td className="py-1.5 pr-3 text-right font-mono text-amber-700">{h.missing_for_review}</td>
                      <td className="py-1.5 pr-3 text-right font-mono text-slate-400">{h.skipped}</td>
                      <td className="py-1.5 pr-3 text-right">
                        {h.reverted ? (
                          <span className="text-xs text-slate-400" data-testid={`csi-reverted-${h.batch_id}`}>Reverted</span>
                        ) : (
                          <Button size="sm" variant="ghost" className="h-7 text-rose-600 hover:bg-rose-50" onClick={() => revertImport(h)} data-testid={`csi-revert-${h.batch_id}`}>
                            <Undo2 size={13} className="mr-1" /> Revert
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
        <Card>
          <CardHeader><CardTitle className="text-base">Map columns — <span className="font-normal text-slate-500">{fileName} · {rows.length} rows</span></CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {fields.map((f) => (
              <div key={f.key} className="grid grid-cols-2 gap-3 items-center">
                <label className="text-sm text-slate-700">{f.label}{f.required && <span className="text-red-500"> *</span>}</label>
                <select
                  value={mapping[f.key] || ''}
                  onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value }))}
                  className="w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
                  data-testid={`csi-map-${f.key}`}
                >
                  <option value="">— skip —</option>
                  {columns.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            ))}
            <div className="flex justify-between pt-3">
              <Button variant="ghost" onClick={reset}><ArrowLeft size={14} className="mr-1.5" /> Start over</Button>
              <Button onClick={runResolve} disabled={busy} data-testid="csi-analyze-btn">
                {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <ArrowRight size={14} className="mr-1.5" />} Analyze
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 'reconcile' && summary && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Ready to update" value={summary.ready} tone="emerald" testid="csi-stat-ready" />
            <Stat label="Missing appointment" value={(summary.missing_appointment || []).length} tone="amber" />
            <Stat label="Unmatched carriers" value={(summary.unmatched_carriers || []).length} tone="rose" />
            <Stat label="Unknown agents" value={(summary.unmatched_npns || []).length} tone="rose" />
          </div>

          {(summary.unmatched_carriers || []).length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-sm flex items-center gap-1.5"><AlertTriangle size={14} className="text-rose-500" /> Match these carriers</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <p className="text-xs text-slate-500">The report uses names not in your carrier list. Pick the right carrier — we'll remember it next time.</p>
                {summary.unmatched_carriers.map((c) => (
                  <div key={c.raw} className="grid grid-cols-2 gap-3 items-center">
                    <div className="text-sm"><span className="font-medium">{c.raw}</span> <span className="text-slate-400 text-xs">({c.count} rows)</span></div>
                    <select
                      value={carrierOverrides[c.raw] || ''}
                      onChange={(e) => setCarrierOverrides((s) => ({ ...s, [c.raw]: e.target.value }))}
                      className="w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
                      data-testid={`csi-carrier-${norm(c.raw)}`}
                    >
                      <option value="">— select carrier —</option>
                      {carriers.map((x) => <option key={x.carrier_id || x.id} value={x.carrier_id || x.id}>{x.carrier_name || x.name}</option>)}
                    </select>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {(summary.unmatched_npns || []).length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-sm flex items-center gap-1.5"><AlertTriangle size={14} className="text-rose-500" /> Unknown agents (NPN not found)</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-slate-500">These NPNs aren't in the system. Link one to an existing agent, or leave it — unlinked rows go to the review queue (no agent is created).</p>
                {summary.unmatched_npns.map((a) => (
                  <div key={a.npn} className="border rounded-md p-2">
                    <div className="text-sm mb-1"><span className="font-mono">{a.npn}</span> · {a.name || 'Unknown'} <span className="text-slate-400 text-xs">({a.count} rows)</span></div>
                    {agentOverrides[a.npn] ? (
                      <div className="flex items-center justify-between text-sm text-emerald-700">
                        <span>Linked → NPN {agentOverrides[a.npn]}</span>
                        <Button variant="ghost" size="sm" onClick={() => setAgentOverrides((s) => { const n = { ...s }; delete n[a.npn]; return n; })}>Unlink</Button>
                      </div>
                    ) : (
                      <div>
                        <input
                          value={agentQuery[a.npn] || ''}
                          onChange={(e) => searchAgent(a.npn, e.target.value)}
                          placeholder="Search agent by name…"
                          className="w-full h-8 rounded-md border border-slate-200 px-2 text-sm"
                          data-testid={`csi-agent-search-${a.npn}`}
                        />
                        {(agentResults[a.npn] || []).length > 0 && (
                          <ul className="mt-1 border rounded-md divide-y">
                            {agentResults[a.npn].map((ag) => (
                              <li key={ag.npn}>
                                <button
                                  className="w-full text-left px-2 py-1.5 text-sm hover:bg-slate-50"
                                  onClick={() => setAgentOverrides((s) => ({ ...s, [a.npn]: ag.npn }))}
                                >
                                  {ag.first_name} {ag.last_name} <span className="text-slate-400 font-mono text-xs">· {ag.npn}</span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {(summary.unmapped_status || []).length > 0 && (
            <Card><CardContent className="py-3 text-sm text-amber-700">
              Unmapped statuses will be skipped: {summary.unmapped_status.map((s) => `${s.status} (${s.count})`).join(', ')}
            </CardContent></Card>
          )}

          <label className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 border rounded-md px-3 py-2 cursor-pointer" data-testid="csi-create-missing-toggle">
            <input type="checkbox" checked={createMissing} onChange={(e) => setCreateMissing(e.target.checked)} className="h-4 w-4" />
            Create the appointment when the agent doesn't have one (instead of sending to review)
          </label>

          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => setStep('map')}><ArrowLeft size={14} className="mr-1.5" /> Back</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={runResolve} disabled={busy} data-testid="csi-reanalyze-btn">Re-analyze</Button>
              <Button onClick={commit} disabled={busy || !allCarriersResolved} data-testid="csi-commit-btn">
                {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <CheckCircle2 size={14} className="mr-1.5" />}
                Apply updates
              </Button>
            </div>
          </div>
          {!allCarriersResolved && (
            <p className="text-xs text-rose-500 text-right">Match all carriers before applying (or re-analyze to skip them).</p>
          )}
        </div>
      )}

      {step === 'done' && result && (
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><CheckCircle2 className="text-emerald-600" size={18} /> Import complete</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <Stat label="Updated" value={result.updated} tone="emerald" testid="csi-done-updated" />
              <Stat label="Created" value={result.created} tone="emerald" testid="csi-done-created" />
              <Stat label="Unchanged" value={result.unchanged} tone="slate" />
              <Stat label="Sent to review" value={result.missing_for_review} tone="amber" />
              <Stat label="Skipped" value={result.skipped} tone="slate" />
            </div>
            {(result.missing_for_review > 0) && (
              <p className="text-xs text-slate-500">{result.missing_for_review} row(s) went to the review queue (unknown agents / missing appointments).</p>
            )}
            <div className="flex gap-2">
              <Button onClick={reset} data-testid="csi-again-btn">Import another report</Button>
              <Button variant="outline" onClick={() => navigate('/contracts/import-status/review')} data-testid="csi-view-review-btn">
                View review queue
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

const TONES = {
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
  rose: 'bg-rose-50 text-rose-700 border-rose-200',
  slate: 'bg-slate-50 text-slate-700 border-slate-200',
};

function Stat({ label, value, tone = 'slate', testid }) {
  return (
    <div className={`rounded-lg border px-3 py-2 ${TONES[tone]}`} data-testid={testid}>
      <div className="text-2xl font-bold">{value ?? 0}</div>
      <div className="text-xs">{label}</div>
    </div>
  );
}

function Stepper({ step }) {
  const steps = [['upload', 'Upload'], ['map', 'Map'], ['reconcile', 'Reconcile'], ['done', 'Done']];
  const idx = steps.findIndex((s) => s[0] === step);
  return (
    <div className="flex items-center gap-2 my-4 text-xs">
      {steps.map((s, i) => (
        <React.Fragment key={s[0]}>
          <span className={`px-2 py-1 rounded-full border ${i <= idx ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-400 border-slate-200'}`}>{i + 1}. {s[1]}</span>
          {i < steps.length - 1 && <span className="text-slate-300">—</span>}
        </React.Fragment>
      ))}
    </div>
  );
}
